import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Block as DeactivateIcon,
  PlayArrow as ActivateIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import platformService from '../../services/platformService';

export default function DealerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dealer, setDealer] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toggleLoading, setToggleLoading] = useState(false);

  // Fetch dealer details
  const fetchDealer = async () => {
    try {
      const response = await platformService.getDealer(id);
      if (response.success) {
        setDealer(response.data.dealer);
      }
    } catch (err) {
      setError('Failed to load dealer details');
      console.error('Fetch dealer error:', err);
    }
  };

  // Fetch tenants belonging to this dealer
  const fetchTenants = async () => {
    try {
      const response = await platformService.getTenants({ page: 1, limit: 50 });
      if (response.success) {
        const dealerTenants = response.data.tenants.filter(
          (t) => t.dealer_id === id
        );
        setTenants(dealerTenants);
      }
    } catch (err) {
      console.error('Fetch tenants error:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDealer(), fetchTenants()]);
      setLoading(false);
    };
    loadData();
  }, [id]);

  const handleToggleActive = async () => {
    setToggleLoading(true);
    try {
      const response = await platformService.updateDealer(id, {
        is_active: !dealer.is_active
      });
      if (response.success) {
        toast.success(dealer.is_active ? 'Dealer deactivated' : 'Dealer activated');
        setDealer(response.data.dealer);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update dealer';
      toast.error(message);
    } finally {
      setToggleLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !dealer) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/dealers')} sx={{ mb: 2 }}>
          Back to Dealers
        </Button>
        <Alert severity="error">{error || 'Dealer not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/dealers')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' } }}>
            {dealer.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={dealer.is_active ? 'Active' : 'Inactive'}
              size="small"
              color={dealer.is_active ? 'success' : 'error'}
            />
          </Box>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          {dealer.is_active ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={toggleLoading ? <CircularProgress size={16} color="inherit" /> : <DeactivateIcon />}
              onClick={handleToggleActive}
              disabled={toggleLoading}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={toggleLoading ? <CircularProgress size={16} color="inherit" /> : <ActivateIcon />}
              onClick={handleToggleActive}
              disabled={toggleLoading}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Activate
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Dealer Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Dealer Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>{dealer.email || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography>{new Date(dealer.created_at).toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Login
                  </Typography>
                  <Typography>
                    {dealer.last_login_at
                      ? new Date(dealer.last_login_at).toLocaleString()
                      : 'Never'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Statistics
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {tenants.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tenants
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tenants Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Tenants
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: { xs: 350, sm: 500 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Users</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No tenants found
                        </TableCell>
                      </TableRow>
                    ) : (
                      tenants.map((tenant) => (
                        <TableRow key={tenant.id}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {tenant.company_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              color={tenant.is_active ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {tenant.userCount ?? 0}
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {new Date(tenant.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/tenants/${tenant.id}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
